import { Card } from '@client/components/ui/card';
import { cn } from '@client/lib/utils';
import {
  putLtcFinancialPlanningStateByClientIdRequest,
  setPolicyPreference,
  setPolicyPreferences,
} from '@client/model';
import {
  selectIsQuestionnaireComplete,
  selectLtcFinancialPlanning,
  selectPolicyPreferences,
} from '@client/model/selectors';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IntroView } from './IntroView';
import { QuestionView } from './QuestionView';
import { SummaryView } from './SummaryView';
import { PREFERENCE_QUESTIONS, TOTAL_QUESTIONS } from './questions';

type ViewState = 'intro' | 'question' | 'summary';

interface PolicyPreferenceCardProps {
  className?: string;
  variant?: 'default' | 'warning';
  warningMessage?: {
    title: string;
    description: string;
  };
}

export function PolicyPreferenceCard({
  className,
  variant,
  warningMessage,
}: PolicyPreferenceCardProps) {
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId: string | undefined }>();
  const policyPreferences = useSelector(selectPolicyPreferences);
  const ltcFinancialPlanningState = useSelector(selectLtcFinancialPlanning);
  const isComplete = useSelector(selectIsQuestionnaireComplete);

  const [viewState, setViewState] = useState<ViewState>(() => {
    if (isComplete) return 'summary';
    return 'intro';
  });
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isComplete && viewState !== 'summary' && !isEditing) {
      setViewState('summary');
    }
  }, [isComplete, viewState, isEditing]);

  const handleGetStarted = useCallback(() => {
    setQuestionIndex(0);
    setIsEditing(true);
    setViewState('question');
  }, []);

  const handleSkip = useCallback(() => {
    dispatch(
      setPolicyPreferences({
        deathBenefit: null,
        paymentStructure: 'noPreference',
        benefitStructure: null,
      }),
    );
    setIsEditing(false);
    setViewState('summary');

    // Save the skipped preferences to backend
    if (clientId) {
      const { isLoading, ...stateToSave } = ltcFinancialPlanningState;
      dispatch(
        putLtcFinancialPlanningStateByClientIdRequest({
          clientId,
          stateData: {
            ...stateToSave,
            policyPreferences: {
              deathBenefit: null,
              paymentStructure: 'noPreference',
              benefitStructure: null,
            },
          },
        }),
      );
    }
  }, [dispatch, clientId, ltcFinancialPlanningState]);

  const handleSelectAnswer = useCallback(
    (value: PolicyPreferenceValue | null) => {
      const currentQuestion = PREFERENCE_QUESTIONS[questionIndex];
      dispatch(setPolicyPreference({ key: currentQuestion.key, value }));

      // Only auto-advance if a value was selected (not deselected)
      if (value !== null) {
        setTimeout(() => {
          if (questionIndex < TOTAL_QUESTIONS - 1) {
            setQuestionIndex(prev => prev + 1);
          } else {
            // Last question answered - save to backend
            setIsEditing(false);
            setViewState('summary');

            if (clientId) {
              const { isLoading, ...stateToSave } = ltcFinancialPlanningState;
              // Update the preference for the current question
              const updatedPreferences = {
                ...stateToSave.policyPreferences,
                [currentQuestion.key]: value,
              };
              dispatch(
                putLtcFinancialPlanningStateByClientIdRequest({
                  clientId,
                  stateData: {
                    ...stateToSave,
                    policyPreferences: updatedPreferences,
                  },
                }),
              );
            }
          }
        }, 200);
      }
    },
    [dispatch, questionIndex, clientId, ltcFinancialPlanningState],
  );

  const handleBack = useCallback(() => {
    if (questionIndex > 0) {
      setQuestionIndex(prev => prev - 1);
    } else {
      setIsEditing(false);
      setViewState(isComplete ? 'summary' : 'intro');
    }
  }, [questionIndex, isComplete]);

  const handleNext = useCallback(() => {
    if (questionIndex < TOTAL_QUESTIONS - 1) {
      setQuestionIndex(prev => prev + 1);
    }
  }, [questionIndex]);

  const handleEdit = useCallback(() => {
    setQuestionIndex(0);
    setIsEditing(true);
    setViewState('question');
  }, []);

  const handleDone = useCallback(() => {
    setIsEditing(false);
    setViewState('summary');

    // Save when user clicks Done while editing
    if (clientId) {
      const { isLoading, ...stateToSave } = ltcFinancialPlanningState;
      dispatch(
        putLtcFinancialPlanningStateByClientIdRequest({
          clientId,
          stateData: stateToSave,
        }),
      );
    }
  }, [dispatch, clientId, ltcFinancialPlanningState]);

  const currentQuestion = PREFERENCE_QUESTIONS[questionIndex];
  const currentValue = currentQuestion ? policyPreferences[currentQuestion.key] : null;

  if (viewState === 'summary') {
    return (
      <div className={className}>
        <AnimatePresence
          mode="wait"
          initial={false}
        >
          <SummaryView
            key="summary"
            preferences={policyPreferences}
            onEdit={handleEdit}
            variant={variant}
            warningMessage={warningMessage}
          />
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Card className={cn('border-primary/20 bg-primary-50 rounded-2xl p-6', className)}>
      <AnimatePresence
        mode="wait"
        initial={false}
      >
        {viewState === 'intro' && (
          <IntroView
            key="intro"
            onGetStarted={handleGetStarted}
            onSkip={handleSkip}
          />
        )}
        {viewState === 'question' && currentQuestion && (
          <QuestionView
            key={`question-${questionIndex}`}
            question={currentQuestion}
            questionIndex={questionIndex}
            selectedValue={currentValue}
            onSelectAnswer={handleSelectAnswer}
            onBack={handleBack}
            onNext={handleNext}
            onDone={handleDone}
          />
        )}
      </AnimatePresence>
    </Card>
  );
}
